export type CashTransferType =
  | 'INTERNAL'
  | 'CONTRIBUTION'
  | 'WITHDRAWAL'
  | 'FEE'
  | 'DIVIDEND';

const SUPPORTED_TRANSFER_TYPES = new Set<CashTransferType>([
  'INTERNAL',
  'CONTRIBUTION',
  'WITHDRAWAL',
  'FEE',
  'DIVIDEND',
]);

/**
 * Resolves the cash movement direction from persisted fields in authority
 * order. transferType is canonical; eventType and legacy transactionType are
 * retained as fallbacks for older rows.
 */
export function resolveCashTransferType(transaction: any): CashTransferType {
  const candidates = [
    transaction?.transferType,
    transaction?.eventType,
    transaction?.transactionType,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toUpperCase();
    if (normalized === 'DEPOSIT') {
      return 'CONTRIBUTION';
    }
    if (SUPPORTED_TRANSFER_TYPES.has(normalized as CashTransferType)) {
      return normalized as CashTransferType;
    }
  }

  return 'INTERNAL';
}

/**
 * Reconstructs a persisted cash ticket for the form. For external movements,
 * the canonical trade portfolio is the applicable source or destination when
 * the specialized portfolio-name field is absent from the DTO.
 */
export function hydrateCashTransferTransaction(
  transaction: any,
  defaultDate: string,
): Record<string, any> {
  const transferType = resolveCashTransferType(transaction);
  const canonicalPortfolioName = transaction?.portfolioName || '';
  const canonicalPortfolioId = transaction?.portfolioId ?? null;

  const sourceUsesCanonicalPortfolio =
    transferType === 'WITHDRAWAL' || transferType === 'FEE';
  const destinationUsesCanonicalPortfolio =
    transferType === 'CONTRIBUTION' || transferType === 'DIVIDEND';

  return {
    transactionType: 'CASH_TRANSFER',
    transferType,
    assetManager: transaction?.assetManagerName || '',
    portfolioManager: transaction?.portfolioManagerName || '',
    assetClass: 'Cash',
    assetSubClass: transaction?.assetSubClass || 'Cash Transfer',
    sourcePortfolio:
      transaction?.sourcePortfolioName
      || (sourceUsesCanonicalPortfolio ? canonicalPortfolioName : ''),
    sourcePortfolioId:
      transaction?.sourcePortfolioId
      ?? (sourceUsesCanonicalPortfolio ? canonicalPortfolioId : null),
    destinationPortfolio:
      transaction?.destinationPortfolioName
      || (destinationUsesCanonicalPortfolio ? canonicalPortfolioName : ''),
    destinationPortfolioId:
      transaction?.destinationPortfolioId
      ?? (destinationUsesCanonicalPortfolio ? canonicalPortfolioId : null),
    amount: transaction?.amount?.toString() || '',
    currency: transaction?.currency || 'USD',
    tradeDate: transaction?.tradeDate || defaultDate,
    settleDate: transaction?.settleDate || defaultDate,
    valueDate: transaction?.valueDate || defaultDate,
    transferReason: transaction?.transferReason || '',
    referenceNumber: transaction?.referenceNumber || '',
    externalAccount: transaction?.externalAccount || '',
    bankName: transaction?.bankName || '',
    bankAccountNumber: transaction?.bankAccountNumber || '',
    bankRoutingNumber: transaction?.bankRoutingNumber || '',
    wireReference: transaction?.wireReference || '',
    submittedBy: transaction?.createdBy || '',
    executedBy: transaction?.executedBy || '',
    approvedBy: transaction?.approvedBy || '',
    notes: transaction?.notes || '',
    rationale: transaction?.rationale || '',
  };
}
